/**
 * Track Chat Page — שיחה כללית
 *
 * The free-form chat for a track, for questions that don't belong to any one
 * topic. Goes through the same store action as the topic chat, just without a
 * topic slug, so the allowance meter and cap handling stay in one place.
 */

import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, ArrowRight } from 'lucide-react'
import MessageBubble from '../../components/chat/MessageBubble'
import TypingIndicator from '../../components/chat/TypingIndicator'
import ChatInput from '../../components/chat/ChatInput'
import { topicIcon } from '../../components/learning/topicIcons'
import { useLearningStore } from '../../store/learningStore'
import { useAuthStore } from '../../store/authStore'
import { TRACKS, type TrackKey } from '../../config/tracks'

interface TrackChatPageProps {
  track: TrackKey
}

export default function TrackChatPage({ track }: TrackChatPageProps) {
  const { user } = useAuthStore()
  const config = TRACKS[track]
  const {
    trackInfo,
    chatMessages,
    chatLoading,
    chatError,
    capReached,
    allowance,
    sendTopicMessage,
    fetchTopics,
    clearTopic,
  } = useLearningStore()
  const containerRef = useRef<HTMLDivElement>(null)

  // Starts a fresh general thread, and loads the track's name + allowance meter.
  useEffect(() => {
    clearTopic()
    void fetchTopics(track)
  }, [track, clearTopic, fetchTopics])

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [chatMessages, chatLoading])

  const handleSend = (text: string) => {
    void sendTopicMessage(track, null, text)
  }

  const Icon = topicIcon(trackInfo?.icon || config.Icon.displayName)
  const title = trackInfo?.name || config.name

  return (
    <div className="mx-auto flex h-full w-full max-w-3xl flex-col">
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white p-4">
        <Link
          to={config.basePath}
          className="text-primary-700 hover:text-primary-900"
          aria-label={`חזרה ל${title}`}
        >
          <ArrowRight size={20} aria-hidden="true" />
        </Link>
        <div className="rounded-full bg-primary-100 p-2 text-primary-700">
          <Icon size={22} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900">שיחה כללית — {title}</h1>
          <p className="text-xs text-gray-600">מידע כללי בלבד — לא ייעוץ אישי</p>
        </div>
        {allowance && (
          <div className="text-start text-xs text-gray-500" aria-live="polite">
            <div>נותר בערך:</div>
            <div className="font-mono font-semibold text-gray-700">
              ${Math.max(0, allowance.remainingUSD).toFixed(3)}
            </div>
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        className="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={`שיחה על ${title}`}
      >
        {chatMessages.length === 0 && (
          <div className="mt-10 px-6 text-center text-gray-500">
            <Icon size={40} className="mx-auto mb-3 text-primary-400" aria-hidden="true" />
            <p className="font-medium text-gray-700">
              שלום{user?.firstName ? ` ${user.firstName}` : ''}!
            </p>
            <p className="mt-2 text-sm">
              {trackInfo?.chatIntro || 'אפשר לשאול כל שאלה בנושא.'}
            </p>
          </div>
        )}
        {chatMessages.map((m, i) => (
          <MessageBubble key={i} message={m} userName={user?.firstName} />
        ))}
        {chatLoading && <TypingIndicator />}
      </div>

      {chatError && (
        <div className="flex items-center gap-2 bg-red-50 p-3 text-sm text-red-700" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          {chatError}
        </div>
      )}

      <div className="border-t border-gray-200 bg-white">
        {capReached ? (
          <p className="bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {config.capReachedMessage}
          </p>
        ) : (
          <ChatInput
            onSend={handleSend}
            disabled={chatLoading}
            hideQuotaInfo
            placeholder={`שאל/י כל שאלה על ${config.shortName}...`}
          />
        )}
      </div>
    </div>
  )
}
